import { createFocusTrap } from "../utils/focus-trap";

function Swipe(elem, callback) {
  var self = this;
  this.callback = callback;

  function handleEvent(e) {
    self.touchHandler(e);
  }

  elem.addEventListener("touchstart", handleEvent, false);
  elem.addEventListener("touchmove", handleEvent, false);
  elem.addEventListener("touchend", handleEvent, false);
}

Swipe.prototype.touches = {
  touchstart: { x: -1, y: -1 },
  touchmove: { x: -1, y: -1 },
  touchend: false,
  direction: "undetermined",
};

Swipe.prototype.touchHandler = function (event) {
  var touch;
  if (typeof event !== "undefined") {
    if (typeof event.touches !== "undefined") {
      touch = event.touches[0];
      switch (event.type) {
        case "touchstart":
        case "touchmove":
          this.touches[event.type].x = touch.pageX;
          this.touches[event.type].y = touch.pageY;
          break;
        case "touchend":
          this.touches[event.type] = true;
          var x = this.touches.touchstart.x - this.touches.touchmove.x,
            y = this.touches.touchstart.y - this.touches.touchmove.y;
          if (x < 0) x /= -1;
          if (y < 0) y /= -1;
          if (x > y)
            this.touches.direction =
              this.touches.touchstart.x < this.touches.touchmove.x
                ? "right"
                : "left";
          else
            this.touches.direction =
              this.touches.touchstart.y < this.touches.touchmove.y
                ? "down"
                : "up";
          this.callback(event, this.touches.direction);
          break;
      }
    }
  }
};

export default class Lightbox {
  constructor({
    lazyload = true,
    counter = true,
    arrows = true,
    slideSpeed = 400,
    ...options
  }) {
    if (!options.selector) {
      console.error(
        'Please add a valid css selector with the option "selector:"'
      );
    } else if (typeof options.selector !== "string") {
      console.error(
        options.selector,
        `is not a string but a(n) ${typeof options.selector}`
      );
    } else {
      this.selector = options.selector;
      this.lazyload = lazyload;
      this.counter = counter;
      this.arrows = arrows;
      this.slideSpeed = slideSpeed;

      this.links = Array.from(
        document.querySelectorAll(`a${options.selector}`)
      );
      this.offsets = [];
      this.nodes = {};
      this.imageIndex = null;
      this.lastFocusedElement = null;
      this.focusTrap = null;
      this.handleKeydown = this.handleKeydown.bind(this);
      if (this.links.length > 0) {
        this.createLightbox();
        this.createNodes();
        this.eventListeners(this.links);
      } else {
        console.error(
          `The selector '${this.selector}' did not yield results. Please make sure the selector is applied on an 'a' element.`
        );
      }
    }
  }

  getImageAlt(link) {
    const dataAlt = link.getAttribute("data-alt");
    if (dataAlt && dataAlt.trim() !== "") {
      return dataAlt.trim();
    }
    const caption = link.getAttribute("data-caption");
    if (caption && caption.trim() !== "") {
      return caption.trim();
    }
    return "";
  }

  goTo = (num, event) => {
    const { items, counter } = this.nodes;
    if (this.counter) {
      counter.innerHTML = `${num + 1}/${this.links.length}`;
    }
    items[num].classList.add("is-active");
    items[num].classList.add("is-loaded");

    for (let i = 0; i < this.offsets.length; i++) {
      const offset = this.offsets[i] - num * 100;

      items[i].style.transform = `translateX(${offset}vw)`;

      if (event) {
        if (event.target.className === "js-gallery-thumb") {
          items[i].style.transition = `opacity 0.4s ease`;
        } else {
          items[i].style.transition = `transform ${this.slideSpeed}ms ease-out`;
        }
      }
    }

    this.updateSlideVisibility(num);
  };

  updateSlideVisibility(activeIndex) {
    const { items } = this.nodes;
    items.forEach((item, index) => {
      const hidden = index !== activeIndex;
      item.setAttribute("aria-hidden", hidden ? "true" : "false");
    });
  }

  createNodes = () => {
    Object.assign(this.nodes, {
      lightboxNode: document.querySelector(".js-gallery"),
      items: Array.from(document.querySelectorAll(".js-gallery-slide")),
      next: document.querySelector(".js-gallery-next"),
      prev: document.querySelector(".js-gallery-prev"),
      close: document.querySelector(".js-gallery-close"),
    });

    Object.assign(this.nodes, {
      counter: document.querySelector(".js-gallery-counter"),
    });

    this.focusTrap = createFocusTrap(this.nodes.lightboxNode);
  };

  handleKeydown(e) {
    switch (e.key) {
      case "ArrowLeft":
        this.goToPrev(e);
        break;
      case "ArrowRight":
        this.goToNext(e);
        break;
      case "Escape":
        this.closeBox();
        break;
      default:
        break;
    }
  }

  openBox(index, event) {
    const { lightboxNode } = this.nodes;
    this.lastFocusedElement = document.activeElement;
    lightboxNode.classList.add("is-active");
    lightboxNode.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
    this.imageIndex = index;
    this.goTo(index, event);
    this.setNav(index);
    document.addEventListener("keydown", this.handleKeydown);
    this.focusTrap.activate();
    this.focusTrap.focusFirst();
  }

  eventListeners = (links) => {
    const { lightboxNode, items, next, prev, close } = this.nodes;
    links.forEach((item, index) => {
      item.addEventListener("click", (e) => {
        e.preventDefault();
        this.openBox(index, e);
      });
    });

    next.addEventListener("click", (e) => {
      this.goToNext(e);
    });

    prev.addEventListener("click", (e) => {
      this.goToPrev(e);
    });

    close.addEventListener("click", () => {
      this.closeBox();
    });

    items.forEach((item) => {
      const img = item.querySelector("img");
      if (!img) return;
      new Swipe(img, (e, direction) => {
        e.preventDefault();
        switch (direction) {
          case "left":
            this.goToNext(e);
            break;
          case "right":
            this.goToPrev(e);
            break;
          default:
            break;
        }
      });
    });
  };

  setNav = (index) => {
    if (this.arrows) {
      const { next, prev } = this.nodes;
      if (index < this.links.length - 1) {
        next.classList.add("is-active");
      }
      if (index > 0) {
        prev.classList.add("is-active");
      }
      if (index <= 0) {
        prev.classList.remove("is-active");
      }
    }
  };

  goToNext = (e) => {
    const { items } = this.nodes;
    if (this.imageIndex < items.length - 1) {
      this.goTo(this.imageIndex + 1, e);
      setTimeout(() => {
        items[this.imageIndex - 1].classList.remove("is-active");
      }, this.slideSpeed);
      this.imageIndex += 1;
      this.setNav(this.imageIndex);
    } else {
      this.goTo(0, e);
      setTimeout(() => {
        items[items.length - 1].classList.remove("is-active");
      }, this.slideSpeed);
      this.imageIndex = 0;
      this.setNav(this.imageIndex);
    }
  };

  goToPrev = (e) => {
    const { items } = this.nodes;
    if (this.imageIndex > 0) {
      this.goTo(this.imageIndex - 1, e);
      setTimeout(() => {
        items[this.imageIndex + 1].classList.remove("is-active");
      }, this.slideSpeed);
      this.imageIndex -= 1;
      this.setNav(this.imageIndex);
    }
  };

  closeBox = () => {
    const { lightboxNode, items } = this.nodes;
    lightboxNode.classList.remove("is-active");
    lightboxNode.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "auto";
    document.removeEventListener("keydown", this.handleKeydown);
    this.focusTrap.deactivate();

    if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === "function") {
      this.lastFocusedElement.focus();
    }
    this.lastFocusedElement = null;

    setTimeout(() => {
      items.forEach((item) => item.classList.remove("is-active"));
    }, this.slideSpeed);
  };

  renderImages = (images) => {
    const imagesLinks = images.map((item, index) => {
      const offset = index * 100;
      this.offsets.push(offset);
      const caption = item.getAttribute("data-caption") || "";
      const alt = this.getImageAlt(item);
      const imageSrcset = item.getAttribute("data-srcset");
      const imageSrc = item.getAttribute("data-src");
      const isActive = index === 0;
      return `
                <li class='c-gallery__slide js-gallery-slide' style='transform: translateX(${offset}vw)' aria-hidden='${isActive ? "false" : "true"}'>
                    ${
                      this.lazyload
                        ? `
                        <img src='${imageSrc}' srcset='data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==' data-srcset='${imageSrcset}' data-sizes='auto' class='lazyload' alt='${alt.replace(/'/g, "&#39;")}' />
                    `
                        : `
                        <img src='${imageSrc}' alt='${alt.replace(/'/g, "&#39;")}'/>
                    `
                    }
                    <figcaption class="t-small"><span>${caption}</span></figcaption>
                </li>
            `;
    });
    return imagesLinks;
  };

  createLightbox = () => {
    const galleryTitle =
      document.querySelector(".c-project-modal__title")?.textContent ||
      document.querySelector(".js-project-modal-title")?.textContent ||
      "Image gallery";

    const lightbox = `
            <div class='c-gallery js-gallery' role='dialog' aria-modal='true' aria-label='${galleryTitle.replace(/'/g, "&#39;")}' aria-hidden='true'>
                <div class='c-gallery__controls t-small'>
                    <button type="button" class="c-gallery-close t-link js-gallery-close">Close</button>
                    <button type="button" class="c-gallery-arrow-area prev u-has-glyph u-has-glyph--left js-gallery-prev" aria-label="Previous image"></button>
                    <button type="button" class="c-gallery-arrow-area next u-has-glyph u-has-glyph--right js-gallery-next" aria-label="Next image"></button>
                    <div class="c-gallery-title"> ${galleryTitle} </div>
                </div>
                <ul class='c-gallery__slider'>
                    ${this.renderImages(this.links).join("")}
                </ul>
                <div class='c-gallery__counter t-small js-gallery-counter'>
                </div>
            </div>
        `;
    document.querySelector("#main").insertAdjacentHTML("afterend", lightbox);
  };
}
